/**
 * 網路硬碟分享 API Service（#24 Phase 2）。
 * 分享 = access-grant 指標；copy-to-my-drive = 在自己個人硬碟生新一份。
 */
import axiosInstance from '../../../../api/axios';

const BASE = '/api/ee/drive-share';

export type SourceKind = 'my_drive' | 'tenant_drive';

export interface ShareMine {
  id: number;
  file_object_id: number;
  filename: string;
  to_user_id: number;
  to_username?: string | null;
  permission: string;
  note?: string | null;
  created_at?: string | null;
  revoked_at?: string | null;
}

export interface ShareInbox {
  id: number;
  file_object_id: number;
  filename: string;
  size_bytes: number;
  mime_type?: string | null;
  from_user_id: number;
  from_username?: string | null;
  permission: string;
  note?: string | null;
  created_at?: string | null;
  resource_kind?: 'file' | 'folder';   // Phase 2.5
  shared_to_kind?: 'user' | 'org';      // Phase 2.5
}

export interface TenantUser {
  id: number;
  username?: string | null;
  account?: string | null;
}

export interface SharedFolderChild {
  child_id: number;
  filename: string;
  size_bytes: number;
  mime_type?: string | null;
  relative_path: string;
}

interface ListEnvelope<T> { total: number; items: T[]; }

export const createShare = async (payload: {
  source_kind: SourceKind;
  source_id: number;
  resource_kind?: 'file' | 'folder';       // Phase 2.5
  shared_to_kind?: 'user' | 'org';          // Phase 2.5
  to_user_id?: number;
  to_org_id?: number;
  permission?: 'read';
  note?: string | null;
}): Promise<ShareMine> => {
  const res = await axiosInstance.post<ShareMine>(BASE, { permission: 'read', ...payload });
  return res.data;
};

export const listMine = async (): Promise<ShareMine[]> => {
  const res = await axiosInstance.get<ListEnvelope<ShareMine>>(`${BASE}/mine`);
  return res.data.items;
};

export const listInbox = async (): Promise<ShareInbox[]> => {
  const res = await axiosInstance.get<ListEnvelope<ShareInbox>>(`${BASE}/inbox`);
  return res.data.items;
};

export const revokeShare = async (shareId: number): Promise<void> => {
  await axiosInstance.delete(`${BASE}/${shareId}`);
};

export const copyToMyDrive = async (shareId: number): Promise<{ ok: boolean; filename: string }> => {
  const res = await axiosInstance.post(`${BASE}/${shareId}/copy-to-my-drive`);
  return res.data;
};

export const getTenantUsers = async (): Promise<TenantUser[]> => {
  const res = await axiosInstance.get<TenantUser[]>(`${BASE}/tenant-users`);
  return res.data;
};

// ── Phase 2.5：組織收到 / 資料夾展開 / 預覽 / 收進租戶硬碟 ──
export const listOrgInbox = async (): Promise<ShareInbox[]> => {
  const res = await axiosInstance.get<ListEnvelope<ShareInbox>>(`${BASE}/org-inbox`);
  return res.data.items;
};

export const listSharedFolder = async (shareId: number): Promise<SharedFolderChild[]> => {
  const res = await axiosInstance.get<{ share_id: number; total: number; items: SharedFolderChild[] }>(
    `${BASE}/${shareId}/folder`);
  return res.data.items;
};

// 預覽：走 axios（帶 Bearer）取 blob → caller 建 objectURL 開新分頁（raw URL 無法帶 auth header）
export const fetchPreviewBlob = async (shareId: number, childId?: number): Promise<Blob> => {
  const res = await axiosInstance.get(`${BASE}/${shareId}/preview`, {
    params: childId != null ? { child_id: childId } : {},
    responseType: 'blob',
  });
  return res.data;
};

export const openPreview = async (shareId: number, childId?: number): Promise<void> => {
  const blob = await fetchPreviewBlob(shareId, childId);
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

export const copyToTenantDrive = async (shareId: number): Promise<{ ok: boolean; copied: number }> => {
  const res = await axiosInstance.post(`${BASE}/${shareId}/copy-to-tenant-drive`);
  return res.data;
};
